import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  createSpecialty,
  deleteSpecialty,
  listSpecialties,
} from "@/api/catalog";
import { usePermission } from "@/auth/usePermission";

export function SpecialtiesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canWrite = usePermission("specialty:write");

  const { data, isLoading } = useQuery({
    queryKey: ["specialties"],
    queryFn: () => listSpecialties(),
  });

  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#1976d2");
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createSpecialty({ code, label, color }),
    onSuccess: () => {
      setShowForm(false);
      setCode("");
      setLabel("");
      setColor("#1976d2");
      setServerError(null);
      qc.invalidateQueries({ queryKey: ["specialties"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSpecialty(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["specialties"] }),
  });

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      {canWrite && !showForm && (
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setShowForm(true)} sx={{ mb: 2 }}>
          {t("settings.addSpecialty")}
        </Button>
      )}
      {showForm && (
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
          {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
            <TextField label={t("settings.code")} value={code} onChange={(e) => setCode(e.target.value)} size="small" required />
            <TextField label={t("settings.label")} value={label} onChange={(e) => setLabel(e.target.value)} size="small" required sx={{ flex: 1 }} />
            <TextField label={t("settings.color")} value={color} onChange={(e) => setColor(e.target.value)} size="small" type="color" sx={{ width: 100 }} />
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
              <Button variant="contained" onClick={() => createMutation.mutate()} disabled={!code || !label || createMutation.isPending}>
                {t("common.save")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <List>
        {(data ?? []).map((s) => (
          <ListItem
            key={s.id}
            divider
            secondaryAction={
              canWrite && (
                <Tooltip title={t("common.cancel")}>
                  <IconButton edge="end" color="error" onClick={() => {
                    if (confirm(t("settings.deleteConfirm"))) deleteMutation.mutate(s.id);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            }
          >
            <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: s.color ?? "grey.400", mr: 2 }} />
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{s.code}</Typography>
                  <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                </Stack>
              }
              secondary={
                <Chip size="small" label={s.is_active ? "ACTIVE" : "ARCHIVED"} color={s.is_active ? "success" : "default"} variant="outlined" />
              }
              slotProps={{ secondary: { component: "div" } }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
